import { AccessDependency, Prisma } from '@prisma/client';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import {
  AccessDependencyDetail,
  AccessWithChildren,
  CreateParentDependencyDto,
  DOMAIN_ACCESS_PARENT_DEPENDENCY_PATH,
  DOMAIN_ACCESS_URL_ROOT,
  SourceAccessItem,
} from '@app/domain-access-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

const ACCESS_ALL_QUERY_KEY = 'ACCESS_ALL_QUERY_KEY';

export const accessKeys = {
  all: [ACCESS_ALL_QUERY_KEY] as const,
  lists: () => [...accessKeys.all, 'list'] as const,
  list: (filters: object) => [...accessKeys.lists(), filters] as const,
  items: () => [...accessKeys.all, 'item'] as const,
  item: (id: number) => [...accessKeys.items(), id] as const,
};

export const useAddAccess = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SourceAccessItem[]) => {
      return axios.post<AccessDependency>(`/${DOMAIN_ACCESS_URL_ROOT}`, { data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKeys.lists() });
    },
  });
};

export const useFindAccess = <T>(
  params: Prisma.AccessFindManyArgs,
  options?: Omit<UseQueryOptions<CommonFindResult<T>>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();
  return useQuery<CommonFindResult<T>>({
    ...options,
    queryKey: accessKeys.list(params),
    queryFn: async () => {
      const { data } = await axios.post(`/${DOMAIN_ACCESS_URL_ROOT}/find`, params);
      return data;
    },
  });
};

export const useFindUniqueAccess = <T>(
  args: Prisma.AccessFindUniqueArgs,
  options?: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();
  return useQuery<T>({
    ...options,
    queryKey: accessKeys.list(args),
    queryFn: async () => {
      const { data } = await axios.post(`/${DOMAIN_ACCESS_URL_ROOT}/find-unique`, args);
      return data;
    },
  });
};

export const useFindAccessById = (
  accessId: number,
  options?: Omit<UseQueryOptions<AccessWithChildren>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<AccessWithChildren>({
    ...options,
    queryKey: accessKeys.item(accessId),
    queryFn: async () => {
      const { data } = await axios.get(`/${DOMAIN_ACCESS_URL_ROOT}/${accessId}`);
      return data;
    },
  });
};

export const useSetPullMembershipThresholdOneTimeOverride = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accessId, value }: { accessId: number; value: boolean }) => {
      return axios.patch<AccessDependency>(
        `/${DOMAIN_ACCESS_URL_ROOT}/${accessId}/set-pull-membership-threshold-one-time-override`,
        { value },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCESS_ALL_QUERY_KEY] });
    },
  });
};

export const useGetAccessDependency = (id: number) => {
  const axios = Axios.useAxiosApi();
  return useQuery<CommonFindResult<AccessDependencyDetail>>({
    queryKey: [...accessKeys.item(id), 'dependencies'],
    queryFn: async () => {
      const { data } = await axios.get(`/${DOMAIN_ACCESS_URL_ROOT}/${id}/${DOMAIN_ACCESS_PARENT_DEPENDENCY_PATH}`);
      return data;
    },
  });
};

export const useEditDependency = (id: number) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateParentDependencyDto) => {
      return axios.patch<AccessDependency>(
        `/${DOMAIN_ACCESS_URL_ROOT}/${id}/${DOMAIN_ACCESS_PARENT_DEPENDENCY_PATH}`,
        dto,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCESS_ALL_QUERY_KEY] });
    },
  });
};

export const useClearDependencies = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => {
      return axios.delete(`/${DOMAIN_ACCESS_URL_ROOT}/${id}/${DOMAIN_ACCESS_PARENT_DEPENDENCY_PATH}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCESS_ALL_QUERY_KEY] });
    },
  });
};

export const useDeleteDependency = (id: number) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accessId: number) => {
      return axios.delete(`/${DOMAIN_ACCESS_URL_ROOT}/${id}/${DOMAIN_ACCESS_PARENT_DEPENDENCY_PATH}/${accessId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCESS_ALL_QUERY_KEY] });
    },
  });
};
